import { fromJS } from 'immutable';
import moment from 'moment';
import CallRecordsResource from 'flow/api/callrecords';
import ConferencesResource from 'flow/api/conferences';
import QueuesResource from 'flow/api/queues';
import RefersResource from 'flow/api/refers';
import SharedExtensionResource from 'flow/api/sharedextension';
import VoicemailsResource from 'flow/api/voicemails';

import Actions from 'flow/actions';
import { selectPbxPageIsMounted } from 'flow/selectors/app-selectors';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import {
    selectQueueCallRecordIsFinished,
    selectQueueCallRecordsActive,
} from 'flow/selectors/call-records-selectors';
import {
    getQueueCallRecordsNextSince,
    selectQueueCallRecordsNextBefore,
    selectQueueCallRecordsWithBPartNextBefore,
    selectQueueMembers,
} from 'flow/selectors/queue-selectors';
import { ISO_8601_MS_RESOLUTION } from 'flow/utils';
import { addHomeLogEntry } from 'flow/utils/browser-utils';
import { getEarliestConnectedCallRecord } from 'flow/utils/call-records-utils';
import {
    CALL_RECORD_INCLUDES_DETAIL,
    CALL_RECORD_INCLUDES_LIST,
} from 'flow/utils/call-record-fetch-includes';
import {
    httpGetErrorDispatcher,
    networkErrorDispatcher,
    spawnVoiceMessageNotification,
} from './app-actions';
import { fetchWidgetChannels } from './channels-actions';

const appFilter = 'desktop';

const createCallback = (dispatch, cbFunc) => {
    return {
        onResult(json) {
            dispatch(cbFunc(json));
        },
        onError: httpGetErrorDispatcher(dispatch),
    };
};

export const fetchQueue = (queueKey) => (dispatch) => {
    QueuesResource.getQueue(
        {
            onResult(json) {
                dispatch(receiveQueue(queueKey, json));
            },
            onError: httpGetErrorDispatcher(dispatch),
        },
        queueKey,
        'all,contact',
        appFilter,
    );
};

export const fetchQueueWithContact = (queueKey) => (dispatch) => {
    QueuesResource.getQueue(
        {
            onResult(json) {
                dispatch(receiveQueue(queueKey, json));
            },
            onError: httpGetErrorDispatcher(dispatch),
        },
        queueKey,
        'contact',
        appFilter,
    );
};

export const fetchQueueCalendar = (queueKey) => (dispatch) => {
    QueuesResource.getCalendar(
        {
            onResult(json) {
                dispatch(receiveQueueCalendar(queueKey, json));
            },
            onError: httpGetErrorDispatcher(dispatch),
        },
        queueKey,
    );
};

export const fetchQueueCallRecords =
    (
        queueEntityKey,
        before,
        since,
        include,
        useErrorDispatcher = true,
        otherPart = null,
    ) =>
    (dispatch) => {
        const otherPartNumberApiRequestFriendly = otherPart
            ? getBPartNumberApiRequestFriendly(otherPart)
            : null;
        return new Promise((resolve, reject) => {
            CallRecordsResource.getQueueCallRecords(
                {
                    onResult(json) {
                        if (otherPart) {
                            dispatch(
                                receiveQueueCallRecordsWithBPart(
                                    queueEntityKey,
                                    otherPart,
                                    json,
                                    include,
                                ),
                            );
                        } else {
                            dispatch(
                                receiveQueueCallRecords(
                                    queueEntityKey,
                                    json,
                                    include,
                                ),
                            );
                        }
                        resolve(json);
                    },
                    onError: (error) => {
                        if (useErrorDispatcher) {
                            httpGetErrorDispatcher(dispatch)(error);
                        }
                        reject(error);
                    },
                    onClientError: (error) => {
                        reject(error);
                    },
                },
                queueEntityKey,
                before,
                since,
                otherPartNumberApiRequestFriendly,
                include,
            );
        });
    };

const getBPartNumberApiRequestFriendly = (number) => {
    if (!number) return number;

    if (number.startsWith('+46')) {
        return number.replace('+46', '0');
    }

    if (number.startsWith('+')) {
        return number.replace('+', '00');
    }

    return number;
};

export const fetchQueueCallRecordsWithBPart =
    (queueEntityKey, bPartNumber, useBeforeArgument = false) =>
    (dispatch, getState) => {
        const beforeArgument = useBeforeArgument
            ? selectQueueCallRecordsWithBPartNextBefore(
                  getState(),
                  queueEntityKey,
                  bPartNumber,
              )
            : null;

        return dispatch(
            fetchQueueCallRecords(
                queueEntityKey,
                beforeArgument,
                null,
                CALL_RECORD_INCLUDES_DETAIL,
                true,
                bPartNumber,
            ),
        ).catch((error) =>
            console.error(
                `Could not fetch call records with bPart ${bPartNumber} for queue ${queueEntityKey} error: ${error}`,
            ),
        );
    };

export const fetchOlderQueueCallRecords =
    (queueEntityKey) => (dispatch, getState) => {
        const nextBefore = selectQueueCallRecordsNextBefore(
            getState(),
            queueEntityKey,
        );
        return dispatch(
            fetchQueueCallRecords(
                queueEntityKey,
                nextBefore,
                null,
                CALL_RECORD_INCLUDES_LIST,
            ),
        ).catch(() =>
            console.error(
                `Could not fetch call records for queue ${queueEntityKey}`,
            ),
        );
    };

// `coverFromMilliseconds` says exactly how far back this fetch has to reach, and
// wins over the anchors below when the caller knows it. Those anchors -- the calls
// we hold as ongoing, else the API's cursor -- know nothing about a call we have
// never seen: the cursor sits one millisecond after the newest call we do know
// about, so a call that started before it can't be reached again. That normally
// can't happen -- a call still running when we fetched is kept as ongoing and
// drags the anchor back behind itself -- but the call log is written per writer
// queue and a queue's calls can be spread across SIP servers, so a later call can
// become visible before an earlier one and move the cursor past it. Asking from a
// known point also keeps the window off the oldest call still ringing or talking,
// which on a busy queue can sit a long way back.
export const fetchNewerQueueCallRecords =
    (queueEntityKey, coverFromMilliseconds) => (dispatch, getState) => {
        const activeCallRecords = selectQueueCallRecordsActive(
            getState(),
            queueEntityKey,
        );
        const earliestConnected =
            getEarliestConnectedCallRecord(activeCallRecords);
        const anchoredSince =
            (earliestConnected &&
                moment(earliestConnected).subtract(1, 'seconds').format('x')) ||
            getQueueCallRecordsNextSince(getState(), queueEntityKey);
        const since = coverFromMilliseconds ?? anchoredSince;
        return dispatch(
            fetchQueueCallRecords(
                queueEntityKey,
                null,
                since,
                CALL_RECORD_INCLUDES_LIST,
            ),
        ).catch(() =>
            console.error(
                `Could not fetch call records for queue ${queueEntityKey}`,
            ),
        );
    };

// Fetches one call record straight into the queue's history. Needs no time window,
// unlike the list endpoint, which can only be asked for calls that *started* within
// one -- so picking up a call that has just ended means asking for everything back
// to when that call started, however long it ran.
export const fetchQueueCallRecord =
    (queueEntityKey, callRecordKey) => (dispatch) =>
        new Promise((resolve) => {
            CallRecordsResource.getCallRecord(
                {
                    onResult(json) {
                        dispatch({
                            type: Actions.RESULT_CALL_RECORD_FOR_SCOPE,
                            scope: { type: 'queue', key: queueEntityKey },
                            callRecordKey,
                            data: json,
                            include: CALL_RECORD_INCLUDES_LIST,
                        });
                        resolve(json);
                    },
                    // The record only exists once the call log has been written, so
                    // a miss is expected and the caller simply asks again.
                    onError: () => resolve(),
                    onClientError: () => resolve(),
                },
                callRecordKey,
                CALL_RECORD_INCLUDES_LIST,
            );
        });

const delay = (milliseconds) =>
    new Promise((resolve) => setTimeout(resolve, milliseconds));

// The history list only shows call records the API reports as finished; a record
// without a `disconnected` is an ongoing call and belongs in the active section
// above it. That distinction is written asynchronously: top hands the call's
// hangup to a background writer of the call log and pushes the hangup event to us
// without waiting for it, so a fetch made the moment we learn the call ended can
// still get the record back as ongoing. It was then stored as an ongoing call and
// stayed out of the history until some unrelated later fetch happened to cover it
// again -- in practice when the next call was answered (AC-1508).
//
// So the fetch on hangup can't be a single shot: ask once more, shortly after. One
// ask is enough because that writer is a serial queue -- either it is keeping up,
// and the write lands within milliseconds of us being a beat early, or it is backed
// up, and no wait we would be willing to make covers it. A record still ongoing
// beyond that usually means a call that hasn't ended at all: a blind transfer hangs
// up the agent's channel while the call carries on with someone else.
const ENDED_QUEUE_CALL_SECOND_ASK_DELAY_MILLISECONDS = 500;

// The join time we know a queue call by comes from top in whole seconds and is not
// the same clock reading as the start the API reports the record's `connected`
// from, so give the window some room rather than trusting the two to line up.
const QUEUE_JOIN_TIME_MARGIN_SECONDS = 15;

// Past this many of a queue's calls being followed to their record at once, calls
// are ending faster than following them one by one can keep up -- a queue being
// emptied, or stuck calls being cleaned up. Following each would mean a fetch per
// call, and none of them can succeed for a call whose record was never finished.
const MAX_QUEUE_CALLS_FOLLOWED_AT_ONCE = 3;

// Which of a queue's calls are being followed, so that the same call reported twice
// doesn't start a second attempt, and so we can tell when there are too many.
const callRecordsBeingFollowed = new Map();

// Queues with a catch-up fetch in flight, so that calls we aren't following one by
// one ask for one window between them rather than one each.
const queuesBeingCaughtUp = new Set();

/**
 * Brings the queue's history up to date after one of its calls ended.
 *
 * `callRecordKey` is the record the ended call will show up as, when we know it.
 * Without it (queue items that carry no locutus session, e.g. omni errands) there
 * is nothing to wait for, so the queue is simply caught up on.
 *
 * `queueJoinTime` is when that call entered the queue, and makes every fetch
 * reach far enough back to include it instead of relying on where the API's
 * cursor happens to sit.
 */
export const onQueueCallEnded =
    (queueEntityKey, callRecordKey, queueJoinTime) =>
    async (dispatch, getState) => {
        const historyIsOnScreen = () =>
            selectPbxPageIsMounted(getState(), queueEntityKey);
        const recordHasLanded = () =>
            selectQueueCallRecordIsFinished(
                getState(),
                queueEntityKey,
                callRecordKey,
            );
        const coverFromMilliseconds = queueJoinTime
            ? moment(queueJoinTime)
                  .subtract(QUEUE_JOIN_TIME_MARGIN_SECONDS, 'seconds')
                  .valueOf()
            : undefined;
        // Ask for the one record when we know which it is; otherwise fall back to a
        // window reaching back to when the call joined the queue.
        const fetchHistory = () =>
            callRecordKey
                ? dispatch(fetchQueueCallRecord(queueEntityKey, callRecordKey))
                : dispatch(
                      fetchNewerQueueCallRecords(
                          queueEntityKey,
                          coverFromMilliseconds,
                      ),
                  );

        // Asks for whatever the queue has gained, covering every call that ended
        // around now rather than one of them. Coalesced, so a flood of endings asks
        // once between them instead of once each.
        const catchUpOnQueueHistory = async (coverFrom) => {
            if (queuesBeingCaughtUp.has(queueEntityKey)) return;
            queuesBeingCaughtUp.add(queueEntityKey);
            try {
                await dispatch(
                    fetchNewerQueueCallRecords(queueEntityKey, coverFrom),
                );
            } finally {
                queuesBeingCaughtUp.delete(queueEntityKey);
            }
        };

        if (!historyIsOnScreen()) return;

        if (!callRecordKey) {
            await catchUpOnQueueHistory(coverFromMilliseconds);
            return;
        }

        // A channel can leave the ongoing calls and come back, so the same call can
        // be reported as over more than once. Having the record already is enough.
        if (recordHasLanded()) return;

        const beingFollowed =
            callRecordsBeingFollowed.get(queueEntityKey) ?? new Set();

        if (beingFollowed.has(callRecordKey)) return;

        if (beingFollowed.size >= MAX_QUEUE_CALLS_FOLLOWED_AT_ONCE) {
            // No cover point: anchoring on the calls the history holds as ongoing
            // reaches back over all of them, not just this one.
            await catchUpOnQueueHistory();
            return;
        }

        beingFollowed.add(callRecordKey);
        callRecordsBeingFollowed.set(queueEntityKey, beingFollowed);

        try {
            await fetchHistory();

            if (recordHasLanded() || !historyIsOnScreen()) return;

            await delay(ENDED_QUEUE_CALL_SECOND_ASK_DELAY_MILLISECONDS);

            if (!historyIsOnScreen()) return;

            await fetchHistory();

            // Logged rather than consoled so that it reaches the app log, which is
            // what we have to go on when this turns up as a customer report. Still
            // being ongoing is not necessarily wrong: a call transferred away
            // carries on after the channel we were watching hung up.
            if (recordHasLanded()) {
                addHomeLogEntry(
                    `Call record ${callRecordKey} of queue ${queueEntityKey} was only reported as finished on the second ask`,
                );
            } else {
                addHomeLogEntry(
                    `Call record ${callRecordKey} of queue ${queueEntityKey} was still reported as ongoing on the second ask, so the queue's history is left without it for now`,
                );
            }
        } finally {
            beingFollowed.delete(callRecordKey);
            if (!beingFollowed.size) {
                callRecordsBeingFollowed.delete(queueEntityKey);
            }
        }
    };

export const createCallRecordAction =
    (callRecordEntityKey, queueEntityKey, agentExtensionDTO, bPartNumber) =>
    (dispatch) => {
        const callActionDTO = {
            agent: { key: agentExtensionDTO.get('key') },
            name: 'handle-unanswered',
            timestamp: moment().format(ISO_8601_MS_RESOLUTION),
        };
        const contactObject = fromJS(agentExtensionDTO.get('contact'));
        CallRecordsResource.createCallAction(
            {
                onResult(json) {
                    json.agent.contact = contactObject;
                    dispatch(
                        receiveCreateCallAction(
                            callRecordEntityKey,
                            queueEntityKey,
                            json,
                            bPartNumber,
                        ),
                    );
                },
                onError: networkErrorDispatcher(dispatch),
            },
            callRecordEntityKey,
            callActionDTO,
        );
    };

export const deleteCallRecordAction =
    (callRecordEntityKey, callActionEntityKey, queueKey, bPartNumber) =>
    (dispatch) => {
        CallRecordsResource.deleteCallAction(
            {
                onResult() {
                    dispatch(
                        receiveDeleteCallAction(
                            callRecordEntityKey,
                            callActionEntityKey,
                            queueKey,
                            bPartNumber,
                        ),
                    );
                },
                onError: networkErrorDispatcher(dispatch),
            },
            callRecordEntityKey,
            callActionEntityKey,
        );
    };

export const clearQueueCallRecords = (queueEntityKey) => ({
    type: Actions.CLEAR_QUEUE_CALL_RECORDS,
    key: queueEntityKey,
});

export const clearQueueCallRecordsBPartSpecific = (
    queueEntityKey,
    bPartNumber,
) => ({
    type: Actions.CLEAR_QUEUE_CALL_RECORDS_BPART_SPECIFIC,
    key: queueEntityKey,
    bPartNumber: bPartNumber,
});

const receiveQueueCallRecords = (queueKey, json, include) => ({
    type: Actions.RESULT_CALL_RECORDS_FETCHED,
    scope: { type: 'queue', key: queueKey },
    data: json,
    include,
});

const receiveQueueCallRecordsWithBPart = (
    queueKey,
    bPartNumber,
    json,
    include,
) => ({
    type: Actions.RESULT_CALL_RECORDS_FETCHED,
    scope: { type: 'queue', key: queueKey, phoneNumber: bPartNumber },
    data: json,
    include,
});

const receiveCreateCallAction = (
    callRecordKey,
    queueKey,
    callActionDTO,
    bPartNumber = null,
) => ({
    type: Actions.RESULT_CREATE_CALL_ACTION,
    callRecordKey,
    queueKey,
    bPartNumber,
    data: callActionDTO,
});
const receiveDeleteCallAction = (
    callRecordKey,
    callActionKey,
    queueKey,
    bPartNumber = null,
) => ({
    type: Actions.RESULT_DELETE_CALL_ACTION,
    callRecordKey,
    callActionKey,
    queueKey,
    bPartNumber,
});

export const fetchQueueStatSummary =
    (
        queueKey,
        serviceLevels,
        resolution,
        errorCallback = networkErrorDispatcher,
    ) =>
    (dispatch) => {
        if (resolution === 'today') resolution = 'day';
        QueuesResource.getQueueStatSummary(
            {
                onResult(json) {
                    dispatch(receiveQueueStatSummary(queueKey, json));
                },
                onError: errorCallback(dispatch),
            },
            queueKey,
            serviceLevels,
            resolution,
        );
    };

export const fetchAllowedQueueMembers = (queueKey) => (dispatch) => {
    QueuesResource.getAllowedMembersForQueue(
        {
            onResult(json) {
                dispatch(receiveAllowedQueueMembers(queueKey, json));
            },
            onError: networkErrorDispatcher(dispatch),
        },
        queueKey,
    );
};

export const activateQueueProfile =
    (queueKey, profileDTO, callback) => (dispatch) => {
        QueuesResource.setActiveProfile(
            {
                onResult(json) {
                    dispatch(receiveQueueProfile(queueKey, json));
                    if (callback) {
                        callback();
                    }
                },

                onError: networkErrorDispatcher(dispatch),
            },
            queueKey,
            profileDTO,
        );
    };

export const fetchQueueActiveProfile =
    (queueKey, errorCallback = httpGetErrorDispatcher) =>
    (dispatch) => {
        QueuesResource.getActiveProfile(
            {
                onResult(json) {
                    dispatch(receiveQueueProfile(queueKey, json));
                },
                onError: errorCallback(dispatch),
            },
            queueKey,
        );
    };

export const updateQueue = (queueKey, queueDTO) => (dispatch) => {
    const queue = {
        ...queueDTO,
        key: queueKey,
    };

    QueuesResource.updateQueue(
        {
            onResult(json) {
                dispatch(receiveQueue(queueKey, json));
            },

            onError: networkErrorDispatcher(dispatch),
            onClientError: (error) => {
                networkErrorDispatcher(dispatch)(error);
                dispatch(fetchQueueWithContact(queueKey));
            },
        },
        queue,
    );
};

export const updateOwnQueueMemberLogin =
    (queueKey, loginValue) => (dispatch, getState) => {
        const ownExtensionKey = selectOwnExtensionKey(getState());
        const queueMemberKey = selectQueueMembers(getState(), queueKey)
            ?.find((member) => member.get('extensionKey') === ownExtensionKey)
            ?.get('key');
        const memberDTO = fromJS({
            key: queueMemberKey,
            loggedIn: loginValue,
        });
        dispatch(updateQueueMember(queueKey, memberDTO));
    };

export const updateQueueMember = (queueKey, queueMemberDTO) => (dispatch) => {
    const queueMemberEntityKey = queueMemberDTO.get('key');

    QueuesResource.updateQueueMember(
        {
            onResult(json) {
                dispatch(
                    receiveQueueMemberUpdate(
                        queueKey,
                        queueMemberEntityKey,
                        json,
                    ),
                );
            },

            onError: networkErrorDispatcher(dispatch),
        },
        queueKey,
        queueMemberEntityKey,
        queueMemberDTO,
    );
};

export const addQueueMembers = (queueKey, queueMemberDTOs) => (dispatch) => {
    QueuesResource.addQueueMembers(
        {
            onResult(json) {
                dispatch(receiveAddQueueMembers(queueKey, json));
            },

            onError: networkErrorDispatcher(dispatch),
        },
        queueKey,
        queueMemberDTOs,
    );
};

export const addQueueWaveMappings =
    (queueKey, queueWaveKey, queueMemberDTOs) => (dispatch) => {
        QueuesResource.addQueueWaveMappings(
            {
                onResult(json) {
                    dispatch(
                        receiveAddQueueWaveMappings(
                            queueKey,
                            queueWaveKey,
                            json,
                        ),
                    );
                },
                onError: networkErrorDispatcher(dispatch),
            },
            queueKey,
            queueWaveKey,
            queueMemberDTOs,
        );
    };

export const removeQueueMember =
    (queueEntityKey, queueMemberEntityKey) => (dispatch) => {
        QueuesResource.removeQueueMember(
            {
                onResult(json) {
                    dispatch(receiveRemoveQueueMember(queueEntityKey, json));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            queueEntityKey,
            queueMemberEntityKey,
        );
    };

export const removeQueueWaveMapping =
    (queueEntityKey, queueWaveMapping) => (dispatch) => {
        QueuesResource.removeQueueWaveMappings(
            {
                onResult(json) {
                    dispatch(
                        receiveRemoveQueueWaveMapping(queueEntityKey, json),
                    );
                },

                onError: networkErrorDispatcher(dispatch),
            },
            queueEntityKey,
            [queueWaveMapping],
        );
    };

export const updateQueueOpeningHours =
    (queueKey, openingHours) => (dispatch) => {
        QueuesResource.setOpeningHours(
            {
                onResult(json) {
                    dispatch(receiveQueueOpeningHours(queueKey, json));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            queueKey,
            openingHours,
        );
    };

export const fetchRefer = (referKey) => (dispatch) => {
    RefersResource.getRefer(
        {
            onResult(json) {
                dispatch(fetchReferCalendar(referKey));
                dispatch(receiveRefer(referKey, json));
            },
            onError: httpGetErrorDispatcher(dispatch),
        },
        referKey,
        'all,live',
        appFilter,
    );
};

export const fetchReferCalendar = (referKey) => (dispatch) => {
    RefersResource.getCalendar(
        {
            onResult(json) {
                dispatch(receiveReferCalendar(referKey, json));
            },
            onError: httpGetErrorDispatcher(dispatch),
        },
        referKey,
    );
};

export const fetchReferProfiles = (referKey) => (dispatch) => {
    RefersResource.getProfiles(
        {
            onResult(json) {
                dispatch(receiveReferProfiles(referKey, json));
            },
            onError: httpGetErrorDispatcher(dispatch),
        },
        referKey,
    );
};

export const updateRefer = (referKey, referDTO) => (dispatch) => {
    const refer = {
        ...referDTO,
        key: referKey,
    };
    RefersResource.setRefer(
        {
            onResult(json) {
                dispatch(receiveRefer(referKey, json));
            },

            onError: networkErrorDispatcher(dispatch),
        },
        refer,
    );
};

export const updateReferOpeningHours =
    (referKey, openingHours) => (dispatch) => {
        RefersResource.setOpeningHours(
            {
                onResult(json) {
                    dispatch(receiveReferOpeningHours(referKey, json));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            referKey,
            openingHours,
        );
    };

export const activateReferProfile =
    (referKey, profileDTO, callback) => (dispatch) => {
        RefersResource.setActiveProfile(
            {
                onResult(json) {
                    dispatch(receiveReferProfile(referKey, json));
                    if (callback) {
                        callback();
                    }
                },

                onError: networkErrorDispatcher(dispatch),
            },
            referKey,
            profileDTO,
        );
    };

export const updateQueueCalendarEvent =
    (entityKey, calendarEventKey, calendarEventDTO) => (dispatch) => {
        QueuesResource.setEvent(
            {
                onResult(json) {
                    dispatch(
                        receiveQueueUpdateCalendarEvent(
                            entityKey,
                            calendarEventKey,
                            json,
                        ),
                    );
                    dispatch(fetchQueue(entityKey));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            entityKey,
            calendarEventKey,
            calendarEventDTO,
        );
    };

export const removeQueueCalendarEvent =
    (entityKey, calendarEventKey) => (dispatch) => {
        QueuesResource.deleteEvent(
            {
                onResult(json) {
                    dispatch(receiveQueueRemoveCalendarEvent(entityKey, json));
                    dispatch(fetchQueue(entityKey));
                    dispatch(fetchQueueCalendar(entityKey));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            entityKey,
            calendarEventKey,
        );
    };

export const createQueueCalendarEvent =
    (entityKey, calendarEvent) => (dispatch) => {
        QueuesResource.addEvent(
            {
                onResult(json) {
                    dispatch(receiveCreateQueueCalendarEvent(entityKey, json));
                    dispatch(fetchQueue(entityKey));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            entityKey,
            calendarEvent,
        );
    };

const receiveCreateQueueCalendarEvent = (entityKey, calenderEventDTO) => ({
    type: Actions.RESULT_QUEUE_ADD_CALENDAR_EVENT,
    entityKey: entityKey,
    data: calenderEventDTO,
});

export const removeReferCalendarEvent =
    (entityKey, calendarEventKey) => (dispatch) => {
        RefersResource.deleteEvent(
            {
                onResult(json) {
                    dispatch(receiveReferRemoveCalendarEvent(entityKey, json));
                    dispatch(fetchRefer(entityKey));
                    dispatch(fetchReferCalendar(entityKey));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            entityKey,
            calendarEventKey,
        );
    };

export const updateReferCalendarEvent =
    (entityKey, calendarEventKey, calendarEventDTO) => (dispatch) => {
        RefersResource.setEvent(
            {
                onResult(json) {
                    dispatch(
                        receiveReferUpdateCalendarEvent(
                            entityKey,
                            calendarEventKey,
                            json,
                        ),
                    );
                    dispatch(fetchRefer(entityKey));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            entityKey,
            calendarEventKey,
            calendarEventDTO,
        );
    };

export const createReferCalendarEvent =
    (entityKey, calendarEvent) => (dispatch) => {
        RefersResource.addEvent(
            {
                onResult(json) {
                    dispatch(receiveCreateReferCalendarEvent(entityKey, json));
                    dispatch(fetchRefer(entityKey));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            entityKey,
            calendarEvent,
        );
    };

export const updateQueueProfile =
    (entityKey, profileKey, profileDTO) => (dispatch) => {
        QueuesResource.updateQueueProfile(
            {
                onResult(json) {
                    dispatch(
                        receiveUpdateQueueProfile(entityKey, profileKey, json),
                    );
                },
                onError: networkErrorDispatcher(dispatch),
            },
            entityKey,
            profileKey,
            profileDTO,
        );
    };

export const fetchActiveOmniErrands = (queueKey) => (dispatch) => {
    QueuesResource.getActiveOmniTasks(
        {
            onResult(json) {
                dispatch(receiveActiveOmniErrands(queueKey, json));
            },
            onError: networkErrorDispatcher(dispatch),
        },
        queueKey,
    );
};

export const receiveTopPbxVoiceMessage =
    (entityKey, voiceMessageDTO, silent = true) =>
    (dispatch) => {
        dispatch(fetchSharedVoicemail(entityKey));

        if (!silent) {
            dispatch(spawnVoiceMessageNotification(voiceMessageDTO));
        }
    };

const receiveUpdateQueueProfile = (entityKey, profileKey, profileDTO) => ({
    type: Actions.RESULT_QUEUE_PROFILE_UPDATE,
    entityKey: entityKey,
    profileKey: profileKey,
    data: profileDTO,
});

const receiveCreateReferCalendarEvent = (entityKey, calenderEventDTO) => ({
    type: Actions.RESULT_REFER_ADD_CALENDAR_EVENT,
    entityKey: entityKey,
    data: calenderEventDTO,
});

export const fetchQueuesWithPromise = (include) => (dispatch) => {
    include = include ? `apps,contact,${include}` : 'apps,contact';
    return new Promise((resolve, reject) => {
        QueuesResource.getSimpleQueues(
            {
                onResult(json) {
                    dispatch(receiveQueues(json));
                    resolve();
                },
                onClientError: (/*error*/) => {
                    reject();
                },
                onError: (/**/) => {
                    networkErrorDispatcher(dispatch);
                    reject();
                },
            },
            include,
            appFilter,
        );
    });
};
export const fetchQueues = (include) => {
    include = include ? `apps,contact,${include}` : 'apps,contact';

    return (dispatch) => {
        const cb = createCallback(dispatch, receiveQueues);
        QueuesResource.getSimpleQueues(cb, include, appFilter);
    };
};

// Lightweight variant that skips contact data (used for refreshing
// queue member login state on reconnection — existing contact data
// in the store is preserved via mergeDeep in the RESULT_QUEUES handler)
export const fetchQueuesWithoutContact = () => {
    return (dispatch) => {
        const cb = createCallback(dispatch, receiveQueues);
        QueuesResource.getSimpleQueues(cb, 'apps', appFilter);
    };
};

export const reloadRefers = () => {
    return (dispatch) => {
        const cb = createCallback(dispatch, receiveRefers);
        RefersResource.getSimpleRefers(cb, 'apps', appFilter);
    };
};

export const fetchSharedVoicemail =
    (entityKey, ...include) =>
    (dispatch) => {
        VoicemailsResource.getVoicemail(
            {
                onResult(json) {
                    dispatch(receiveSharedVoicemail(entityKey, json));
                },
                onError: httpGetErrorDispatcher(dispatch),
            },
            entityKey,
            include,
            appFilter,
        );
    };

const reloadSharedVoicemails = () => {
    return (dispatch) => {
        const cb = createCallback(dispatch, receiveSharedVoicemails);
        VoicemailsResource.getVoicemails(cb, 'contact,all', appFilter);
    };
};

export const updateSharedVoicemail =
    (entityKey, voicemailDTO) => (dispatch) => {
        VoicemailsResource.updateVoicemail(
            {
                onResult(json) {
                    dispatch(receiveSharedVoicemail(entityKey, json));
                },

                onError: networkErrorDispatcher(dispatch),
            },
            voicemailDTO,
        );
    };

export const removeSharedVoiceMessage = (entityKey, uniqueId) => (dispatch) => {
    VoicemailsResource.removeVoiceMessage(
        {
            onResult(/*json*/) {
                dispatch(receiveVoiceMessageDelete(entityKey, uniqueId));
            },

            onError: networkErrorDispatcher(dispatch),
        },
        entityKey,
        uniqueId,
    );
};

export const removeAllSharedVoiceMessages = (entityKey) => (dispatch) => {
    VoicemailsResource.removeAllVoiceMessages(
        {
            onResult(/*json*/) {
                dispatch(receiveVoiceMessageDeleteAll(entityKey));
            },
            onError: networkErrorDispatcher(dispatch),
        },
        entityKey,
    );
};

export const setSharedVoiceMessageRead =
    (voicemailKey, voiceMessageUniqueID) => (dispatch) => {
        dispatch(
            receiveSetSharedVoicemessageRead(
                voicemailKey,
                voiceMessageUniqueID,
            ),
        );
    };

const receiveSetSharedVoicemessageRead = (
    voicemailKey,
    voiceMessageUniqueID,
) => ({
    type: Actions.SET_SHARED_VOICEMESSAGE_READ,
    voicemailKey,
    voiceMessageUniqueID,
});

const receiveSharedVoicemail = (entityKey, voicemailDTO) => ({
    type: Actions.RESULT_SHARED_VOICEMAIL,
    key: entityKey,
    data: voicemailDTO,
});

const receiveVoiceMessageDelete = (entityKey, uniqueId) => ({
    type: Actions.RESULT_SHARED_VOICEMESSAGE_DELETE,
    key: entityKey,
    data: {
        uniqueId: uniqueId,
    },
});

const receiveVoiceMessageDeleteAll = (entityKey) => ({
    type: Actions.RESULT_SHARED_VOICEMESSAGES_DELETE_ALL,
    key: entityKey,
});

export const reloadConferences = () => {
    return (dispatch) => {
        const cb = createCallback(dispatch, receiveConferences);
        ConferencesResource.getConferences(cb, 'contact,live');
    };
};

const receiveQueues = (json) => (dispatch, getState) =>
    dispatch({
        type: Actions.RESULT_QUEUES,
        data: json,
        ownExtensionKey: selectOwnExtensionKey(getState()),
    });

export const receiveQueueMemberUpdate = (queueKey, memberKey, json) => {
    return {
        type: Actions.RESULT_QUEUE_MEMBER_UPDATE,
        queueKey: queueKey,
        memberKey: memberKey,
        data: json,
    };
};

const receiveAddQueueMembers = (queueKey, json) => (dispatch, getState) =>
    dispatch({
        type: Actions.RESULT_ADD_QUEUE_MEMBERS,
        queueKey: queueKey,
        data: json,
        ownExtensionKey: selectOwnExtensionKey(getState()),
    });

const receiveAddQueueWaveMappings = (queueKey, queueWaveKey, json) => {
    return {
        type: Actions.RESULT_ADD_QUEUE_WAVE_MAPPINGS,
        queueKey: queueKey,
        queueWaveKey: queueWaveKey,
        data: json,
    };
};

const receiveRemoveQueueMember = (queueKey, json) => (dispatch, getState) =>
    dispatch({
        type: Actions.RESULT_REMOVE_QUEUE_MEMBERS,
        queueKey: queueKey,
        data: json,
        ownExtensionKey: selectOwnExtensionKey(getState()),
    });

const receiveRemoveQueueWaveMapping = (queueKey, json) => {
    return {
        type: Actions.RESULT_REMOVE_QUEUE_WAVE_MAPPING,
        queueKey: queueKey,
        data: json,
    };
};

export const receiveQueueProfile = (queueKey, json) => ({
    type: Actions.RESULT_QUEUE_PROFILE,
    key: queueKey,
    data: json,
});

const receiveQueueOpeningHours = (queueKey, json) => ({
    type: Actions.RESULT_QUEUE_OPENING_HOURS,
    key: queueKey,
    data: json,
});

const receiveQueueUpdateCalendarEvent = (
    entityKey,
    originalCalendarEventKey,
    calenderEventDTO,
) => ({
    type: Actions.RESULT_QUEUE_UPDATE_CALENDAR_EVENT,
    entityKey: entityKey,
    originalCalendarEventKey: originalCalendarEventKey,
    data: calenderEventDTO,
});

const receiveQueueRemoveCalendarEvent = (entityKey, calenderEventDTO) => ({
    type: Actions.RESULT_QUEUE_REMOVE_CALENDAR_EVENT,
    entityKey: entityKey,
    data: calenderEventDTO,
});

const receiveQueueCalendar = (queueKey, json) => ({
    type: Actions.RESULT_QUEUE_CALENDAR,
    key: queueKey,
    data: json,
});

const receiveQueueStatSummary = (queueKey, json) => ({
    type: Actions.RESULT_QUEUE_STAT_SUMMARY,
    key: queueKey,
    data: json,
});

const receiveQueue = (queueKey, json) => {
    return {
        type: Actions.RESULT_QUEUE,
        key: queueKey,
        data: json,
    };
};

const receiveAllowedQueueMembers = (queueKey, json) => ({
    type: Actions.RESULT_ALLOWED_QUEUE_MEMBERS,
    key: queueKey,
    data: json,
});

const receiveReferOpeningHours = (referKey, json) => ({
    type: Actions.RESULT_REFER_OPENING_HOURS,
    key: referKey,
    data: json,
});

const receiveReferUpdateCalendarEvent = (
    entityKey,
    originalCalendarEventKey,
    calenderEventDTO,
) => ({
    type: Actions.RESULT_REFER_UPDATE_CALENDAR_EVENT,
    entityKey: entityKey,
    originalCalendarEventKey: originalCalendarEventKey,
    data: calenderEventDTO,
});

const receiveReferRemoveCalendarEvent = (entityKey, calenderEventDTO) => ({
    type: Actions.RESULT_REFER_REMOVE_CALENDAR_EVENT,
    entityKey: entityKey,
    data: calenderEventDTO,
});

const receiveReferProfile = (referKey, json) => ({
    type: Actions.RESULT_REFER_PROFILE,
    key: referKey,
    data: json,
});

const receiveRefers = (json) => {
    return {
        type: Actions.RESULT_REFERS,
        data: json,
    };
};

const receiveRefer = (referKey, json) => ({
    type: Actions.RESULT_REFER,
    key: referKey,
    data: json,
});

const receiveReferCalendar = (referKey, json) => ({
    type: Actions.RESULT_REFER_CALENDAR,
    key: referKey,
    data: json,
});

const receiveReferProfiles = (referKey, json) => ({
    type: Actions.RESULT_REFER_PROFILES,
    key: referKey,
    data: json,
});

const receiveSharedVoicemails = (json) => {
    return {
        type: Actions.RESULT_SHARED_VOICEMAILS,
        data: json,
    };
};

const receiveActiveOmniErrands = (queueKey, json) => {
    return {
        type: Actions.RESULT_ACTIVE_OMNI_ERRANDS,
        queue: queueKey,
        data: json,
    };
};

export const updateConference = (conferenceDTO) => (dispatch) => {
    const conferenceKey = conferenceDTO.get('key');
    ConferencesResource.updateConference(
        {
            onResult(json) {
                dispatch(receiveConference(conferenceKey, json));
            },

            onError: networkErrorDispatcher(dispatch),
        },
        conferenceKey,
        conferenceDTO,
    );
};

const receiveConference = (conferenceKey, json) => {
    return {
        type: Actions.RESULT_CONFERENCE,
        key: conferenceKey,
        data: json,
    };
};

const receiveConferences = (json) => {
    return {
        type: Actions.RESULT_CONFERENCES,
        data: json,
    };
};

export const fetchSharedExtensions = () => (dispatch) => {
    SharedExtensionResource.getSharedExtensions(
        {
            onResult(json) {
                dispatch(receiveSharedExtensions(json));
            },
            onError: httpGetErrorDispatcher(dispatch),
        },
        '',
        appFilter,
    );
};

const receiveSharedExtensions = (json) => {
    return {
        type: Actions.RESULT_SHARED_EXTENSION,
        data: json,
    };
};

export { receiveQueue };

export const reloadPbx = () => {
    return (dispatch) => {
        dispatch(fetchQueues());
        dispatch(reloadRefers());
        dispatch(reloadSharedVoicemails());
        dispatch(fetchSharedExtensions());
        dispatch(fetchWidgetChannels('apps'));
    };
};
// TODO: WEB-14413 - Needed only because of GUI bug. Remove when voicemails can be fetched without admin flag.
export const reloadNonAdminPbx = () => {
    return (dispatch) => {
        dispatch(fetchQueues());
        dispatch(reloadRefers());
        dispatch(fetchSharedExtensions());
        dispatch(fetchWidgetChannels('apps'));
    };
};
